import { useMutation } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { useCallback, useMemo, useState } from 'react';
import Toast from 'react-native-root-toast';

import {
	CHECKLIST_PURPOSE,
	MY_DAY_CONFIRMATION_KEY,
	PICK_LIST_RECALCULATION_MODE,
	STATUS,
} from '../../../common/constants';
import {
	RECALCULATE_AND_TOP_UP_ALL_WARNING,
	RECALCULATE_UNFINISHED_PACKS_WARNING,
} from '../../../common/constants/text-constants';
import { CONFIRM_RETURNED_TO_WAREHOUSE } from '../../../common/graphql/clockEvent';
import {
	CONFIRM_MY_DAY_STEP,
	MAKE_SERVICE_DOCKETS_NEXT_STOPS,
	RECALCULATE_SERVICE_RUN_PICKLISTS,
} from '../../../common/graphql/serviceRun';
import { Alert } from '../../components/other/Alert';
import { useOfflineMutation } from '../../offline';
import { vibrateConfirm } from '../../utils/feedback';
import useAcknowledgeServiceRunBrief from '../servicerun/useAcknowledgeServiceRunBrief';
import useServiceRunVehicleUpdate from '../servicerun/useServiceRunVehicleUpdate';
import { DRIVER_ROUTE } from './driverRoutes';
import useStopActions from './useStopActions';

/** Step keys whose completion is a plain My day confirmation (confirmStepByKey). */
export const STEP_CONFIRMATION_KEYS = {
	runOverview: MY_DAY_CONFIRMATION_KEY.RUN_OVERVIEW,
	accessKeys: MY_DAY_CONFIRMATION_KEY.ACCESS_KEYS,
	ticketsSummary: MY_DAY_CONFIRMATION_KEY.TICKETS_SUMMARY,
	moneyBags: MY_DAY_CONFIRMATION_KEY.MONEY_BAGS,
	returnKeys: MY_DAY_CONFIRMATION_KEY.RETURN_KEYS,
	returnStock: MY_DAY_CONFIRMATION_KEY.RETURN_STOCK,
};

/** Step keys completed by submitting a checklist template (openStepChecklist). */
export const STEP_CHECKLISTS = {
	leaveWarehouseChecklist: { purpose: CHECKLIST_PURPOSE.LEAVE_WAREHOUSE, fallbackTitle: 'Leave warehouse checklist' },
	vehicleChecklist: { purpose: CHECKLIST_PURPOSE.VEHICLE_END_OF_DAY, fallbackTitle: 'Vehicle end of day checklist' },
	selfDebrief: { purpose: CHECKLIST_PURPOSE.SELF_DEBRIEF, fallbackTitle: 'Self debrief' },
};

/**
 * Every driver day handler (navigation to step pages and the My day mutations),
 * extracted from the classic My day board without behaviour change.
 *
 * Params:
 * - day: the useDriverDayModel / useDriverDay() result
 * - options.docket: the stop this screen works on (default day.activeDocket);
 *   the manual restock flow and onPerStopStepPress act on it
 * Training (day.trainingContext) only applies to stops of the loaded driver
 * day: stop actions for any other stop send no myDayTraining/adminTraining.
 * - options.stopRoute: route opened by openStopDetails (default 'StopView';
 *   the legacy board passes 'DocketDetailView')
 * - options.onStepConfirmed: called after a confirmation, vehicle change or
 *   return to warehouse succeeds (the board clears its focus override)
 * - options.captureMedia / options.capturedMediaBatch: from withMediaPicker,
 *   needed only by screens that offer openQuickPhoto
 */
const useDriverDayActions = (day, {
	docket: docketOption,
	stopRoute = DRIVER_ROUTE.STOP,
	onStepConfirmed,
	captureMedia,
	capturedMediaBatch,
} = {}) => {
	const navigation = useNavigation();
	const {
		serviceRun,
		isTraining,
		isOnline,
		trainingContext,
		trainingRouteParams,
		clockOutReminder,
		sortedDockets,
		activeDocket,
		upcomingDocket,
		routeMapPoints,
		isDocketDone,
		setSelectedDocketId,
		getDocketNavigationParams,
		canEditPickLists,
		refetchRuns,
		refetchAll,
	} = day;
	const docket = docketOption === undefined ? activeDocket : docketOption;
	const [recalculatingPickListsMode, setRecalculatingPickListsMode] = useState(null);

	const handleStepConfirmed = useCallback(() => {
		if (onStepConfirmed) {
			onStepConfirmed();
		}
	}, [onStepConfirmed]);

	const requireTrainingConnection = useCallback(() => {
		if (isTraining && !isOnline) {
			Toast.show('Reconnect to continue training');
			return false;
		}

		return true;
	}, [isOnline, isTraining]);

	// Training belongs to the training run only: a stop outside the loaded
	// driver day (another run opened from Service runs, see useStopDocket)
	// never gets the day's training params or adminTraining, so it behaves as
	// it would in DocketDetailView.
	const dayDocketIds = useMemo(() => new Set(sortedDockets.map(item => item.id)), [sortedDockets]);
	const isTrainingStop = useCallback(targetDocket => Boolean(isTraining &&
		targetDocket?.id &&
		dayDocketIds.has(targetDocket.id)), [dayDocketIds, isTraining]);
	const getStopTrainingParams = useCallback(targetDocket => (
		isTrainingStop(targetDocket) ? trainingRouteParams : {}
	), [isTrainingStop, trainingRouteParams]);
	const requireStopConnection = useCallback(targetDocket => (
		!isTrainingStop(targetDocket) || requireTrainingConnection()
	), [isTrainingStop, requireTrainingConnection]);
	const getStopNavigationParams = useCallback((targetDocket) => {
		const params = { ...getDocketNavigationParams(targetDocket) };
		delete params.myDayTraining;
		return { ...params, ...getStopTrainingParams(targetDocket) };
	}, [getDocketNavigationParams, getStopTrainingParams]);

	const stopActions = useStopActions({
		docket,
		planned: docket?.serviceRun?.planned || serviceRun?.planned,
		trainingContext,
		isTrainingTarget: isTrainingStop,
		onChanged: refetchRuns,
		captureMedia,
		capturedMediaBatch,
	});
	const {
		openPickList,
		openConfirmStockLevels,
		openConfirmValues,
		openTickets,
		openPhotos,
		openQuickPhoto,
		openCreateTicket,
		manualRestock,
	} = stopActions;

	const openStopDetails = useCallback((targetDocket) => {
		if (!targetDocket || !requireStopConnection(targetDocket)) {
			return;
		}
		// Incomplete stops become current; the selection effect immediately
		// clears a genuinely done stop while its detail screen stays open.
		setSelectedDocketId(targetDocket.id);
		navigation.push(stopRoute, getStopNavigationParams(targetDocket));
	}, [getStopNavigationParams, navigation, requireStopConnection, setSelectedDocketId, stopRoute]);

	const openFullRouteMap = useCallback(() => {
		navigation.push('DriverRouteMapView', { points: routeMapPoints });
	}, [navigation, routeMapPoints]);

	const goToNextStop = useCallback(() => {
		if (!upcomingDocket?.id) {
			return;
		}

		setSelectedDocketId(upcomingDocket.id);
	}, [setSelectedDocketId, upcomingDocket]);

	const openClockPass = useCallback(() => {
		navigation.push('ClockPassView');
	}, [navigation]);

	const openPickListSummary = useCallback(() => {
		if (serviceRun?.id && requireTrainingConnection()) {
			navigation.push('PickListSummaryView', { id: serviceRun.id, ...trainingRouteParams });
		}
	}, [navigation, requireTrainingConnection, serviceRun?.id, trainingRouteParams]);

	const openChecklist = useCallback((purpose, fallbackTitle, stepPosition) => {
		navigation.push('ChecklistFormView', {
			purpose,
			serviceRunId: serviceRun?.id,
			vehicleId: serviceRun?.vehicle?.id,
			fallbackTitle,
			stepPosition,
		});
	}, [navigation, serviceRun?.id, serviceRun?.vehicle?.id]);
	// stepPosition ({ number, total }, optional) shows "Step n of m" in the checklist header.
	const openStepChecklist = useCallback((stepKey, stepPosition) => {
		const checklist = STEP_CHECKLISTS[stepKey];
		if (checklist) {
			openChecklist(checklist.purpose, checklist.fallbackTitle, stepPosition);
		}
	}, [openChecklist]);

	const [recalculateServiceRunPicklists] = useMutation(RECALCULATE_SERVICE_RUN_PICKLISTS);

	const runPickListRecalculation = useCallback(async (mode) => {
		if (!requireTrainingConnection()) {
			return;
		}

		setRecalculatingPickListsMode(mode);
		try {
			await recalculateServiceRunPicklists({
				variables: {
					serviceRunId: serviceRun.id,
					mode,
				},
			});
			await refetchRuns();
		}
		catch (error) {
			Toast.show(error?.message || 'Unable to recalculate packs');
		}
		finally {
			setRecalculatingPickListsMode(null);
		}
	}, [recalculateServiceRunPicklists, refetchRuns, requireTrainingConnection, serviceRun?.id]);

	const openRecalculatePicklistAlert = useCallback((mode) => {
		if (!canEditPickLists || !serviceRun?.id) {
			return;
		}
		const isTopUpAll = mode === PICK_LIST_RECALCULATION_MODE.TOP_UP_ALL;
		const actionLabel = isTopUpAll
			? 'Recalculate and top up all'
			: 'Recalculate unfinished packs';

		Alert.alert(
			actionLabel,
			isTopUpAll
				? RECALCULATE_AND_TOP_UP_ALL_WARNING
				: RECALCULATE_UNFINISHED_PACKS_WARNING,
			[
				{ text: 'Cancel', onPress: () => {}, style: 'cancel' },
				{
					text: actionLabel,
					onPress: () => runPickListRecalculation(mode),
				},
			],
			{ cancelable: false },
		);
	}, [canEditPickLists, runPickListRecalculation, serviceRun?.id]);

	const [confirmMyDayStepMutation, { loading: isConfirmingStep }] = useOfflineMutation(CONFIRM_MY_DAY_STEP, {
		configKey: 'confirmMyDayStep',
		disableOffline: isTraining,
		onCompleted: () => {
			vibrateConfirm();
			handleStepConfirmed();
			refetchRuns();
		},
		onError: error => Toast.show(error?.message || 'Unable to save the confirmation'),
	});
	// items: ticked item ids for Keys and site access / Return keys (getRunAccessItems).
	const confirmStep = useCallback(async (confirmationKey, items) => {
		if (!serviceRun?.id || !requireTrainingConnection()) {
			return;
		}

		try {
			const result = await confirmMyDayStepMutation({
				variables: {
					id: serviceRun.id,
					stepKey: confirmationKey,
					...(isTraining ? { adminTraining: true } : {}),
					...(items ? { items } : {}),
				},
			});
			// Queued offline: the optimistic cache update advances the board and
			// onCompleted (online path) does not fire, so reset the focus here.
			if (result?.offline) {
				vibrateConfirm();
				handleStepConfirmed();
				Toast.show('Confirmation saved; will sync when back online');
			}
		}
		catch (error) {
			Toast.show(error?.message || 'Unable to save the confirmation');
		}
	}, [confirmMyDayStepMutation, handleStepConfirmed, isTraining, requireTrainingConnection, serviceRun?.id]);
	const confirmStepByKey = useCallback((stepKey, items) => {
		if (STEP_CONFIRMATION_KEYS[stepKey]) {
			return confirmStep(STEP_CONFIRMATION_KEYS[stepKey], items);
		}
	}, [confirmStep]);

	const [confirmOnlineMyDayStepMutation, { loading: isConfirmingOnlineStep }] = useMutation(CONFIRM_MY_DAY_STEP, {
		onCompleted: () => {
			vibrateConfirm();
			handleStepConfirmed();
			refetchRuns();
		},
	});
	const [confirmReturnedToWarehouseMutation, { loading: isConfirmingReturnedToWarehouse }] = useMutation(CONFIRM_RETURNED_TO_WAREHOUSE);
	const confirmReturnedToWarehouse = useCallback(async () => {
		if (!isOnline || !clockOutReminder?.canConfirmReturnedToWarehouse) {
			return;
		}

		try {
			await confirmReturnedToWarehouseMutation();
			vibrateConfirm();
			handleStepConfirmed();
			await refetchAll();
		}
		catch (error) {
			Toast.show(error?.message || 'Unable to confirm that you returned to the warehouse');
		}
	}, [
		clockOutReminder?.canConfirmReturnedToWarehouse,
		confirmReturnedToWarehouseMutation,
		handleStepConfirmed,
		isOnline,
		refetchAll,
	]);
	const confirmOnlineStep = useCallback(async (confirmationKey, errorMessage) => {
		if (!serviceRun?.id || !isOnline) {
			return;
		}

		try {
			await confirmOnlineMyDayStepMutation({
				variables: {
					id: serviceRun.id,
					stepKey: confirmationKey,
					...(isTraining ? { adminTraining: true } : {}),
				},
			});
		}
		catch (error) {
			refetchRuns();
			Toast.show(error?.message || errorMessage);
		}
	}, [confirmOnlineMyDayStepMutation, isOnline, isTraining, refetchRuns, serviceRun?.id]);
	const markPickTopUpNotNeeded = useCallback(() => confirmOnlineStep(
		MY_DAY_CONFIRMATION_KEY.PICK_TOP_UP_NOT_NEEDED,
		'Unable to mark top-up not needed',
	), [confirmOnlineStep]);
	const confirmTubsLoaded = useCallback(() => confirmOnlineStep(
		MY_DAY_CONFIRMATION_KEY.TUBS_LOADED,
		'Unable to confirm tubs are loaded',
	), [confirmOnlineStep]);
	const startServiceRun = useCallback(() => {
		if (serviceRun?.myDayPickTopUpStatus?.markedNotNeeded) {
			return confirmOnlineStep(
				MY_DAY_CONFIRMATION_KEY.SERVICE_RUN_STARTED,
				'Unable to start the service run',
			);
		}

		return confirmStep(MY_DAY_CONFIRMATION_KEY.SERVICE_RUN_STARTED);
	}, [confirmOnlineStep, confirmStep, serviceRun?.myDayPickTopUpStatus?.markedNotNeeded]);

	const [makeNextStopsMutation, { loading: isMakingNextStop }] = useMutation(MAKE_SERVICE_DOCKETS_NEXT_STOPS, {
		onError: error => Toast.show(error?.message || 'Unable to change the stop order'),
	});
	// Resolves true once the new order is saved (callers close their reorder UI).
	const submitNextStops = useCallback(async (serviceDocketIds, reason) => {
		if (!serviceRun?.id || !requireTrainingConnection()) {
			return false;
		}

		try {
			await makeNextStopsMutation({
				variables: {
					serviceRunId: serviceRun.id,
					serviceDocketIds,
					reason,
					exactOrder: true,
					...(isTraining ? { adminTraining: true } : {}),
				},
			});
		}
		catch (error) {
			return false;
		}

		const docketById = new Map(sortedDockets.map(item => [item.id, item]));
		let reorderableIndex = 0;
		const firstIncompleteDocket = sortedDockets
			.map(item => (
				item.status === STATUS.COMPLETE || item.startedProcessing
					? item
					: docketById.get(serviceDocketIds[reorderableIndex++])
			))
			.find(item => item && !isDocketDone(item));

		// Follow the persisted first unfinished stop, including a fixed in-progress
		// stop when one sits before the reordered remaining route.
		setSelectedDocketId(firstIncompleteDocket?.id || null);
		refetchRuns();
		return true;
	}, [isDocketDone, isTraining, makeNextStopsMutation, refetchRuns, requireTrainingConnection, serviceRun?.id, setSelectedDocketId, sortedDockets]);

	const onPerStopStepPress = useCallback((step) => {
		if (!stopActions.onStepPress(step)) {
			openStopDetails(docket);
		}
	}, [docket, openStopDetails, stopActions]);

	const onVehicleUpdated = useCallback(() => {
		handleStepConfirmed();
		refetchRuns();
	}, [handleStepConfirmed, refetchRuns]);
	const vehicleUpdate = useServiceRunVehicleUpdate({
		serviceRunId: serviceRun?.id,
		adminTraining: isTraining,
		disableActions: isTraining && !isOnline,
		onUpdated: onVehicleUpdated,
	});

	const [acknowledgeBriefMutation, { loading: isAcknowledgingBrief }] = useAcknowledgeServiceRunBrief({
		serviceRunId: serviceRun?.id,
		adminTraining: isTraining,
	});
	// Resolves true once acknowledged (callers close the brief).
	const acknowledgeBrief = useCallback(async () => {
		if (!serviceRun?.id || !requireTrainingConnection()) {
			return false;
		}

		try {
			await acknowledgeBriefMutation();
			refetchRuns();
			return true;
		}
		catch (error) {
			Toast.show(error?.message || 'Unable to acknowledge the brief');
			return false;
		}
	}, [acknowledgeBriefMutation, refetchRuns, requireTrainingConnection, serviceRun?.id]);

	return {
		requireTrainingConnection,
		openStopDetails,
		openPickList,
		openConfirmStockLevels,
		openConfirmValues,
		openTickets,
		openPhotos,
		openQuickPhoto,
		openCreateTicket,
		openFullRouteMap,
		goToNextStop,
		openClockPass,
		openPickListSummary,
		openChecklist,
		openStepChecklist,
		openRecalculatePicklistAlert,
		recalculatingPickListsMode,
		confirmStep,
		confirmStepByKey,
		isConfirmingStep,
		markPickTopUpNotNeeded,
		confirmTubsLoaded,
		startServiceRun,
		isConfirmingOnlineStep,
		confirmReturnedToWarehouse,
		isConfirmingReturnedToWarehouse,
		submitNextStops,
		isMakingNextStop,
		onPerStopStepPress,
		manualRestock,
		selectVehicle: vehicleUpdate.selectVehicle,
		confirmVehicle: vehicleUpdate.confirmVehicle,
		isUpdatingVehicle: vehicleUpdate.loading,
		acknowledgeBrief,
		isAcknowledgingBrief,
	};
};

export default useDriverDayActions;
